import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { useActivityMarketplaceGate, API } from './activityMarketplaceShared';
import ActivitySearch from '../../components/ActivitySearch';
import { ROUTES } from '../../data/stjSite';
import axios from 'axios';

const ActivitiesByCompanyPage = () => {
  const ready = useActivityMarketplaceGate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState([]);
  const [query, setQuery] = useState('');
  const handleFilter = useCallback((next, q) => { setVisible(next); setQuery(q); }, []);

  useEffect(() => {
    setSeoMetadata({
      title: 'Charter Companies',
      description: 'Every St John charter company and excursion operator we book direct with - pick a company to see all of their trips.',
      keywords: 'st john charter companies, st john excursion operators, charters by host st john',
      // the legacy /activities/charters-by-host-st-john-vi URL is the indexed one
      canonicalPath: ROUTES.chartersByHost,
    });
  }, []);

  useEffect(() => {
    if (!ready) return;
    axios.get(`${API}/api/public/tours-charters/sellers`)
      .then((r) => { const list = r.data || []; setSellers(list); setVisible(list); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ready]);

  if (!ready) return <div className="min-h-screen bg-white"><Header /></div>;

  return (
    <div className="min-h-screen bg-white" data-testid="activities-by-company-page">
      <Header />
      <section className="relative overflow-hidden pt-36 pb-12">
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/30 via-transparent to-transparent" />
        <div className="relative mx-auto max-w-6xl px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-teal-300/80">Explore</p>
            <h1 className="mt-2 text-4xl font-black text-slate-900 sm:text-5xl lg:text-6xl">By Charter Company</h1>
            <p className="mt-4 max-w-2xl text-base text-slate-600">Browse activities offered by each charter company. <Link to="/activities" className="text-teal-300 underline hover:text-teal-700">Looking to browse by activity type instead?</Link></p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6">
        <ActivitySearch
          items={sellers}
          onFilter={handleFilter}
          placeholder="Search companies, activities, length of tour or tags..."
          testId="company-search"
        />
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24 pt-10">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-400" /></div>
        ) : sellers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-20 text-center text-slate-600" data-testid="activities-by-company-empty">
            No charter companies yet. Check back soon!
          </div>
        ) : (
          <>
          {query && (
            <p className="mb-4 text-sm font-semibold text-slate-700" data-testid="companies-filtered-count">
              {visible.length} matching compan{visible.length === 1 ? 'y' : 'ies'}
            </p>
          )}
          {visible.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center text-slate-600" data-testid="companies-no-match">
              No charter companies match that search.
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="company-logo-grid">
            {visible.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.04 }}>
                <Link
                  to={`/${s.public_slug || s.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:border-blue-300 hover:shadow-md"
                  data-testid={`public-company-${s.slug}`}
                  title={`${s.name} - see all of their trips`}
                >
                  {/* the logo is shown whole on white, never cropped */}
                  <div className="flex h-28 items-center justify-center bg-white p-4">
                    {s.logo_url ? (
                      <img
                        src={resolveMediaUrl(s.logo_url)}
                        alt={`${s.name} logo`}
                        loading="lazy"
                        className="max-h-20 w-auto max-w-full object-contain transition duration-300 group-hover:scale-105"
                        data-testid={`company-logo-${s.slug}`}
                      />
                    ) : (
                      /* no logo uploaded yet: a clean monogram instead of a broken looking gap */
                      <span
                        className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-blue-800 to-teal-600 text-2xl font-black text-white"
                        data-testid={`company-logo-placeholder-${s.slug}`}
                        aria-hidden="true"
                      >
                        {(s.name || '?')
                          .split(/\s+/)
                          .filter((word) => /[a-z0-9]/i.test(word))
                          .slice(0, 2)
                          .map((word) => word[0].toUpperCase())
                          .join('')}
                      </span>
                    )}
                  </div>
                  <div className="mt-auto border-t border-slate-100 px-3 py-3 text-center">
                    <h3 className="text-sm font-bold leading-snug text-slate-900">{s.name}</h3>
                    <p className="mt-0.5 text-xs font-semibold text-slate-500">
                      {s.activity_count} trip{s.activity_count === 1 ? '' : 's'}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default ActivitiesByCompanyPage;
